import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import {
  ChakraBox,
  ChakraFlex,
  TableFilterElement,
  UniversalSelect,
  FAIconName,
  DatePickerInput,
  InputGroupV2,
  DateFormat,
  DatePickerInputHandle,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import ReactDOM from 'react-dom';

import {
  getDateRangeForOption,
  isDateRangeValid,
} from '~/lib/util/patientDocuments.util';
import { PATIENT_DOCUMENT_UPLOAD_DATE_FILTER_OPTIONS } from '~/lib/constants/documentCenter.constants';
import { PatientDocumentsFilters } from '~/store/services/documents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { AnalyticsEvent } from '~/analytics';

interface UploadDateFilterProps {
  filters: PatientDocumentsFilters;
  onFiltersChange?: (filters: PatientDocumentsFilters) => void;
  id: string;
}

const DATE_RANGE_OPTION = {
  label: 'Date range',
  searchData: 'date range',
  value: 'date_range',
};

const UPLOAD_DATE_FILTER_EVENT_MAP: Record<string, AnalyticsEvent> = {
  last_7_days: DocumentCenterEvents.uploadDateFilterLast7DaysSelected,
  last_30_days: DocumentCenterEvents.uploadDateFilterLast30DaysSelected,
  this_year: DocumentCenterEvents.uploadDateFilterLast1YearSelected,
  date_range: DocumentCenterEvents.uploadDateFilterDateRangeSelected,
};

export function UploadDateFilter({
  filters,
  onFiltersChange,
  id,
}: UploadDateFilterProps) {
  const { trackEvent } = useAnalytics();
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [isDateRangeActive, setIsDateRangeActive] = useState(false);

  const [pickerToOpen, setPickerToOpen] = useState<'from' | 'to' | null>(null);

  const fromPickerRef = useRef<DatePickerInputHandle | null>(null);
  const toPickerRef = useRef<DatePickerInputHandle | null>(null);

  useEffect(() => {
    if (isDateRangeActive && pickerToOpen) {
      if (pickerToOpen === 'from') fromPickerRef.current?.open();
      if (pickerToOpen === 'to') toPickerRef.current?.open();

      setPickerToOpen(null);
    }
  }, [isDateRangeActive, pickerToOpen]);

  useEffect(() => {
    if (filters.uploadStartDate && filters.uploadEndDate) {
      setIsDateRangeActive(false);
    }
  }, [filters.uploadStartDate, filters.uploadEndDate]);

  const applyDateRange = (from: string, to: string) => {
    if (!isDateRangeValid(from, to)) {
      onFiltersChange?.({
        ...filters,
        uploadStartDate: to,
        uploadEndDate: to,
      });
    } else {
      onFiltersChange?.({
        ...filters,
        uploadStartDate: from,
        uploadEndDate: to,
      });
    }
    setIsDateRangeActive(false);
    setCustomFrom(null);
    setCustomTo(null);
  };

  const handleRemoveDates = () => {
    onFiltersChange?.({
      ...filters,
      uploadStartDate: '',
      uploadEndDate: '',
    });
    setCustomFrom(null);
    setCustomTo(null);
    setIsDateRangeActive(false);
  };

  const isDateRangeFromDatepicker =
    filters.uploadStartDate &&
    filters.uploadEndDate &&
    !PATIENT_DOCUMENT_UPLOAD_DATE_FILTER_OPTIONS.some((option) => {
      const range = getDateRangeForOption(option.value);
      return (
        range.from === filters.uploadStartDate &&
        range.to === filters.uploadEndDate
      );
    });
  const popperContainer = useCallback((children: React.ReactNode) => {
    return ReactDOM.createPortal(
      <ChakraBox position="relative" zIndex={9999}>
        {children}
      </ChakraBox>,
      document.body,
    );
  }, []);

  const formattedStartDate =
    filters.uploadStartDate &&
    DateTime.fromISO(filters.uploadStartDate).toFormat(
      DateFormat.mmDdYySlashes,
    );

  const formattedEndDate =
    filters.uploadEndDate &&
    DateTime.fromISO(filters.uploadEndDate).toFormat(DateFormat.mmDdYySlashes);
  const todayISO = DateTime.now().endOf('day').toISO();

  const dateRangeOptionLabel = useMemo(
    () => (
      <ChakraFlex gap="8px">
        <InputGroupV2 id={`${id}__from-group`} label="From">
          <DatePickerInput
            defaultDate={customFrom ? new Date(customFrom) : undefined}
            id={`${id}__startdate-input`}
            maxDate={new Date()}
            onSubmit={(date) => {
              const fromISO = DateTime.fromJSDate(date).startOf('day').toISO();
              if (!fromISO) return;

              setCustomFrom(fromISO);

              if (customTo) {
                applyDateRange(fromISO, customTo);
              } else {
                toPickerRef.current?.open();
              }
            }}
            placeholder="MM/DD/YYYY"
            popperContainer={popperContainer}
            ref={fromPickerRef}
          />
        </InputGroupV2>

        <InputGroupV2 id={`${id}__enddate-group`} label="To">
          <DatePickerInput
            defaultDate={customTo ? new Date(customTo) : undefined}
            id={`${id}__enddate-input`}
            maxDate={new Date()}
            onSubmit={(date) => {
              if (!date) return;
              const toISO = DateTime.fromJSDate(date).endOf('day').toISO();
              if (!toISO) return;

              setCustomTo(toISO);

              applyDateRange(customFrom ?? toISO, toISO);
            }}
            placeholder="MM/DD/YYYY"
            popperContainer={popperContainer}
            ref={toPickerRef}
          />
        </InputGroupV2>
      </ChakraFlex>
    ),
    [id, popperContainer, customFrom, customTo, applyDateRange],
  );
  const handleFilterChipClick = (
    filterKey: 'uploadStartDate' | 'uploadEndDate',
  ) => {
    if (isDateRangeFromDatepicker) {
      setCustomFrom(filters.uploadStartDate || null);
      setCustomTo(filters.uploadEndDate || null);
      setIsDateRangeActive(true);
      setPickerToOpen(filterKey === 'uploadStartDate' ? 'from' : 'to');
    } else {
      handleRemoveDates();
    }
  };

  return (
    <ChakraBox minWidth="150px">
      {filters.uploadStartDate &&
      filters.uploadEndDate &&
      !isDateRangeActive ? (
        <ChakraFlex gap="12px">
          <TableFilterElement
            filter={{
              filterKey: 'uploadStartDate',
              title: 'Upload start date',
              filters: [
                {
                  label: formattedStartDate,
                  value: filters.uploadStartDate,
                },
              ],
            }}
            onRemove={() => handleFilterChipClick('uploadStartDate')}
            rightIcon={
              isDateRangeFromDatepicker
                ? FAIconName.chevronDownRegular
                : FAIconName.timesRegular
            }
          />

          <TableFilterElement
            filter={{
              filterKey: 'uploadEndDate',
              title: 'Upload end date',
              filters: [
                {
                  label: formattedEndDate,
                  value: filters.uploadEndDate,
                },
              ],
            }}
            onRemove={() => handleFilterChipClick('uploadEndDate')}
            rightIcon={
              isDateRangeFromDatepicker
                ? FAIconName.chevronDownRegular
                : FAIconName.timesRegular
            }
          />
        </ChakraFlex>
      ) : (
        <UniversalSelect
          dropdownWidth={380}
          formatOptionLabel={(option) => {
            if (option.value !== DATE_RANGE_OPTION.value) return option.label;
            return dateRangeOptionLabel;
          }}
          id={`${id}__upload-date-filter`}
          isSingleSelect
          menuIsOpen={isDateRangeActive ? true : undefined}
          onBlur={() => {
            if (customFrom && !filters.uploadStartDate) {
              applyDateRange(customFrom, customTo ?? todayISO);
            }
            setIsDateRangeActive(false);
          }}
          onChange={(option) => {
            const event = UPLOAD_DATE_FILTER_EVENT_MAP[option.value];
            if (event) {
              trackEvent(event, {
                Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                selectedOption: option.value,
                selectedOptionLabel: option.label,
              });
            }
            if (!option || option.value === DATE_RANGE_OPTION.value) {
              setIsDateRangeActive(true);
              return;
            }
            const range = getDateRangeForOption(option.value);
            if (range) {
              onFiltersChange?.({
                ...filters,
                uploadStartDate: range.from,
                uploadEndDate: range.to,
              });
              setIsDateRangeActive(false);
            }
          }}
          onMenuOpen={() => {
            trackEvent(DocumentCenterEvents.uploadDateFilterMenuClicked, {
              Source: AnalyticsSourceValues.DOCUMENT_CENTER,
            });
          }}
          options={[
            ...PATIENT_DOCUMENT_UPLOAD_DATE_FILTER_OPTIONS,
            DATE_RANGE_OPTION,
          ]}
          placeholder="Upload date"
          shouldUseCustomDropdownWidth
          value={null}
          variant="slim"
        />
      )}
    </ChakraBox>
  );
}

export default UploadDateFilter;
